import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Sparkles, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import heroImg from "@/assets/hero-boardroom.jpg";
import heroImgAvif640 from "@/assets/hero-boardroom-640.avif";
import heroImgAvif800 from "@/assets/hero-boardroom-800.avif";
import heroImgAvif1200 from "@/assets/hero-boardroom-1200.avif";
import heroImgAvif1600 from "@/assets/hero-boardroom-1600.avif";
import heroAnalytics from "@/assets/hero-analytics.jpg";
import heroAnalyticsAvif640 from "@/assets/hero-analytics-640.avif";
import heroAnalyticsAvif1200 from "@/assets/hero-analytics-1200.avif";
import heroAnalyticsAvif1600 from "@/assets/hero-analytics-1600.avif";
import heroNegotiation from "@/assets/hero-negotiation.jpg";
import heroNegotiationAvif640 from "@/assets/hero-negotiation-640.avif";
import heroNegotiationAvif1200 from "@/assets/hero-negotiation-1200.avif";
import heroNegotiationAvif1600 from "@/assets/hero-negotiation-1600.avif";
import workshopImg from "@/assets/workshop.jpg";
import workshopAvif640 from "@/assets/workshop-640.avif";
import workshopAvif1200 from "@/assets/workshop-1200.avif";
import workshopAvif1600 from "@/assets/workshop-1600.avif";
import paperworkImg from "@/assets/paperwork.jpg";
import negotiationImg from "@/assets/service-negotiation.jpg";
import negotiationImgAvif480 from "@/assets/service-negotiation-480.avif";
import negotiationImgAvif768 from "@/assets/service-negotiation-768.avif";
import auditImg from "@/assets/service-audit.jpg";
import auditImgAvif480 from "@/assets/service-audit-480.avif";
import auditImgAvif768 from "@/assets/service-audit-768.avif";
import skuImg from "@/assets/service-sku.jpg";
import skuImgAvif480 from "@/assets/service-sku-480.avif";
import skuImgAvif768 from "@/assets/service-sku-768.avif";
import partnershipImage from "@/assets/partnership-meeting.jpg.asset.json";
import partnershipAvif480 from "@/assets/partnership-meeting-480.avif.asset.json";
import partnershipAvif800 from "@/assets/partnership-meeting-800.avif.asset.json";
import phmgBg from "@/assets/phmg-bg.jpg";
import phmgBgAvif800 from "@/assets/phmg-bg-800.avif";
import phmgBgAvif1600 from "@/assets/phmg-bg-1600.avif";


type HeroSlide = {
  src: string;
  alt: string;
  avifMobile?: string;
  avifSmall?: string;
  avifMedium?: string;
  avifLarge?: string;
};

const heroSlides: HeroSlide[] = [
  { src: heroImg, avifMobile: heroImgAvif640, avifSmall: heroImgAvif800, avifMedium: heroImgAvif1200, avifLarge: heroImgAvif1600, alt: "Procurement specialists reviewing Salesforce contract analytics" },
  { src: heroAnalytics, avifMobile: heroAnalyticsAvif640, avifSmall: heroAnalyticsAvif1200, avifMedium: heroAnalyticsAvif1200, avifLarge: heroAnalyticsAvif1600, alt: "Two professionals analysing SaaS contract dashboards" },
  { src: heroNegotiation, avifMobile: heroNegotiationAvif640, avifSmall: heroNegotiationAvif1200, avifMedium: heroNegotiationAvif1200, avifLarge: heroNegotiationAvif1600, alt: "Negotiation meeting around a modern boardroom table" },
  { src: workshopImg, avifMobile: workshopAvif640, avifSmall: workshopAvif1200, avifMedium: workshopAvif1200, avifLarge: workshopAvif1600, alt: "Strategy workshop session with the SaaSed team" },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "SaaSed | Salesforce Procurement & Commercial Advisory" },
      { name: "description", content: "SaaSed is a specialist Salesforce procurement and commercial advisory company helping enterprise buyers with renewals, negotiation and cost optimisation." },
      { property: "og:title", content: "SaaSed | Salesforce Procurement & Commercial Advisory" },
      { property: "og:description", content: "SaaSed is a specialist Salesforce procurement and commercial advisory company helping enterprise buyers with renewals, negotiation and cost optimisation." },
      { property: "og:image", content: "https://saased.co/saased-share-image.png" },
      { name: "twitter:image", content: "https://saased.co/saased-share-image.png" },
      { name: "twitter:title", content: "SaaSed | Salesforce Procurement & Commercial Advisory" },
      { name: "twitter:description", content: "SaaSed is a specialist Salesforce procurement and commercial advisory company helping enterprise buyers with renewals, negotiation and cost optimisation." },

      { property: "og:url", content: "https://saased.co/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","@id":"https://saased.co/#faq","mainEntity":[{"@type":"Question","name":"What is Salesforce procurement advisory?","acceptedAnswer":{"@type":"Answer","text":"Salesforce procurement advisory is specialist commercial support for companies that buy Salesforce. An advisor reviews the contract and licence usage, builds the negotiation position and supports the renewal discussions, so the buyer pays for what the business actually uses. SaaSed focuses on Salesforce as a vendor, working with enterprise buyers."}},{"@type":"Question","name":"When should we start preparing for a Salesforce renewal?","acceptedAnswer":{"@type":"Answer","text":"Ideally 12 to 18 months before the contract end date, not when the quote arrives. Starting 8 to 12 months ahead is still a good preparation window, but the leverage you have shrinks with every month after that. Early preparation gives you time to verify your dates, audit your SKUs and build a licence baseline before commercial discussions begin."}},{"@type":"Question","name":"What is a Salesforce SELA?","acceptedAnswer":{"@type":"Answer","text":"A Salesforce SELA, or Enterprise License Agreement, is a single contract that covers a defined set of Salesforce products under one commercial structure for a fixed term. It can simplify buying, but pricing, scope and renewal terms need careful review, because the structure shapes what you pay at every later renewal."}},{"@type":"Question","name":"How do we find out if we are paying for unused Salesforce licences?","acceptedAnswer":{"@type":"Answer","text":"Compare the licences you hold with real user activity: inactive users, unused products and add-ons bought in-term. A software audit maps each licence to actual usage and contract terms, showing where shelfware sits before the renewal. SaaSed's own tool, Dioro, delivers this SKU audit and usage insight."}}]}),
      },
    ],
    links: [
      { rel: "canonical", href: "https://saased.co/" },
      // Start the LCP hero image download immediately, before CSS/JS parse.
      // Mobile gets the small AVIF only; desktop keeps the responsive set.
      {
        rel: "preload",
        as: "image",
        type: "image/avif",
        href: heroImgAvif640,
        media: "(max-width: 767px)",
        fetchPriority: "high",
      },
      {
        rel: "preload",
        as: "image",
        type: "image/avif",
        href: heroImgAvif1200,
        media: "(min-width: 768px)",
        imageSrcSet: `${heroImgAvif800} 800w, ${heroImgAvif1200} 1200w, ${heroImgAvif1600} 1600w`,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],

  }),

  component: HomePage,
});

const services = [
  {
    image: skuImg,
    title: "Contract & SKU Review",
    desc: "Identify pricing inconsistencies, in-term add-ons and opportunities to restructure before renewal.",
  },
  {
    image: negotiationImg,
    title: "Full Negotiation Support",
    desc: "Strategic commercial support before and during Salesforce renewal discussions.",
  },
  {
    image: auditImg,
    title: "Software Audit",
    desc: "Gain visibility into usage, shelfware, inactive users and license alignment.",
  },
];

const painPoints = [
  {
    title: "8/10 Companies",
    body: "overpay for software and SaaS agreements without realizing it.",
  },
  {
    title: "Shelfware Drain",
    body: "33% of SaaS licenses sit unused on the shelf — silently draining your budget every renewal cycle.",
  },
  {
    title: "Product Complexity",
    body: "Constant vendor name changes and confusing feature re-bundling make it nearly impossible to track what you are actually paying for.",
  },
  {
    title: "Premature Scaling",
    body: "Vendors push you to buy capacity for where you want to be in three years, forcing you to pay for tomorrow's ambition today.",
  },
];

/**
 * Schedules non-critical work (observers, timers, animations) after the browser
 * is idle so it never competes with hydration/first paint on the main thread.
 * Returns a cancel function.
 */
function onIdle(cb: () => void, timeout = 2000): () => void {
  if (typeof window === "undefined") return () => {};
  const w = window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (typeof w.requestIdleCallback === "function") {
    const id = w.requestIdleCallback(cb, { timeout });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(cb, 200);
  return () => window.clearTimeout(id);
}


function Typewriter({
  text,
  className,
  speed = 25,
  showCursor = true,
}: {
  text: string;
  className?: string;
  speed?: number;
  showCursor?: boolean;
}) {
  const [typed, setTyped] = useState("");
  const [done, setDone] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    let started = false;
    let observer: IntersectionObserver | null = null;
    const cancelIdle = onIdle(() => {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !started) {
              started = true;
              let i = 0;
              timer = setInterval(() => {
                i++;
                setTyped(text.slice(0, i));
                if (i >= text.length && timer) {
                  clearInterval(timer);
                  setDone(true);
                }
              }, speed);
            }
          });
        },
        { threshold: 0.3 }
      );
      observer.observe(el);
    });
    return () => {
      cancelIdle();
      observer?.disconnect();
      if (timer) clearInterval(timer);
    };
  }, [text, speed]);


  return (
    <span ref={ref} className={className}>
      {typed}
      {showCursor && (
        <span
          aria-hidden
          className={`ml-0.5 inline-block w-[2px] bg-current align-baseline ${done ? "opacity-0" : "animate-pulse"}`}
          style={{ height: "0.9em" }}
        />
      )}
    </span>
  );
}

function SequentialReveal({
  lines,
  pause = 800,
}: {
  lines: string[];
  pause?: number;
}) {
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = false;
    let observer: IntersectionObserver | null = null;
    const cancelIdle = onIdle(() => {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !started) {
              started = true;
              setActiveIdx(0);
            }
          });
        },
        { threshold: 0.3 }
      );
      observer.observe(el);
    });
    return () => {
      cancelIdle();
      observer?.disconnect();
    };
  }, []);


  useEffect(() => {
    if (activeIdx < 0 || activeIdx >= lines.length) return;
    const timer = setTimeout(() => {
      setActiveIdx((idx) => idx + 1);
    }, pause);
    return () => clearTimeout(timer);
  }, [activeIdx, lines.length, pause]);

  return (
    <div ref={ref} className="space-y-10 md:space-y-14">
      {lines.map((line, i) => {
        const visible = i <= activeIdx;
        return (
          <div
            key={i}
            className={`flex items-start gap-5 transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary font-sans text-sm font-bold text-primary-foreground md:h-12 md:w-12 md:text-base">
              {String(i + 1).padStart(2, "0")}
            </span>
            <p className="flex-1 font-sans text-justify pt-1">
              {line}
            </p>
          </div>
        );
      })}
    </div>
  );
}


function HomePage() {
  const [active, setActive] = useState(0);
  const [loadedSlides, setLoadedSlides] = useState<ReadonlySet<number>>(
    () => new Set([0])
  );
  const total = heroSlides.length;

  // Carousel auto-play scheduling.
  // Desktop: unchanged — starts once the browser is idle.
  // Mobile (<md): additionally waits for DOMContentLoaded *and* the first user
  // scroll/interaction so no rotation work lands on the main thread while the
  // LCP hero is still painting (TBT).
  useEffect(() => {
    let id: ReturnType<typeof setInterval> | null = null;
    let cancelIdle: (() => void) | null = null;
    const start = () => {
      cancelIdle = onIdle(() => {
        id = setInterval(() => {
          setActive((i) => {
            const next = (i + 1) % total;
            setLoadedSlides((loaded) => {
              if (loaded.has(next)) return loaded;
              const updated = new Set(loaded);
              updated.add(next);
              return updated;
            });
            return next;
          });
        }, 5000);
      });
    };

    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches;

    if (!isMobile) {
      start();
      return () => {
        cancelIdle?.();
        if (id) clearInterval(id);
      };
    }

    let started = false;
    const kick = () => {
      if (started) return;
      started = true;
      start();
    };
    const events: Array<keyof WindowEventMap> = ["scroll", "touchstart", "pointerdown"];
    events.forEach((e) => window.addEventListener(e, kick, { once: true, passive: true }));
    const fallback = window.setTimeout(kick, 4000);
    const onReady = () => window.setTimeout(kick, 1500);
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", onReady, { once: true });
    }

    return () => {
      events.forEach((e) => window.removeEventListener(e, kick));
      document.removeEventListener("DOMContentLoaded", onReady);
      window.clearTimeout(fallback);
      cancelIdle?.();
      if (id) clearInterval(id);
    };
  }, [total]);



  return (
    <>
      {/* HERO */}
      <section
        className="group relative overflow-hidden md:min-h-[720px] lg:min-h-[720px]"
        style={{ minHeight: "clamp(520px, 70vh, 780px)", contain: "layout paint" }}

      >
        {/* Slides */}
        <div className="absolute inset-0 -z-10">
          {heroSlides.map((s, i) => loadedSlides.has(i) && (
            <picture key={s.src}>
              {s.avifSmall && s.avifLarge && (
                <>
                  {/* Mobile (<md): one 640px AVIF, fastest possible LCP fetch */}
                  <source
                    type="image/avif"
                    media="(max-width: 767px)"
                    srcSet={s.avifMobile ?? s.avifSmall}
                    sizes="100vw"
                  />
                  <source
                    type="image/avif"
                    srcSet={`${s.avifSmall} 800w, ${s.avifMedium ?? s.avifLarge} 1200w, ${s.avifLarge} 1600w`}
                    sizes="(max-width: 768px) 100vw, 100vw"
                  />
                </>
              )}

              <img
                src={s.src}
                alt={s.alt}
                width={1600}
                height={1067}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1500ms] ease-in-out ${i === active ? "opacity-100" : "opacity-0"}`}
                loading={i === 0 ? "eager" : "lazy"}
                decoding={i === 0 ? "sync" : "async"}
                fetchPriority={i === 0 ? "high" : "low"}
              />

            </picture>
          ))}
          {/* Dark overlay for legibility */}
          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent" />
        </div>

        <div className="container-x relative py-24 md:py-36">
          <div className="min-h-[360px] max-w-2xl text-white md:min-h-0">

            <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-widest text-white/80 backdrop-blur">
              <Sparkles size={14} className="text-primary" />
              Hello, we're SaaSed.
            </span>
            <h1 className="mt-5 text-5xl leading-[0.95] md:text-7xl text-white drop-shadow-lg font-serif">
              Your strategic partner for <em className="not-italic text-white/80">Salesforce</em> renewals.
            </h1>
            <div className="mt-[16px] h-1 w-20 rounded-full bg-primary" />
            <p className="mt-[20px]! max-w-xl text-base leading-relaxed text-white/70">
              With 12 years in the Salesforce ecosystem, 8 of them inside Salesforce, we understand how enterprise SaaS sales organisations operate, how commercial structures are shaped, and where customers quietly lose leverage long before negotiations begin.
            </p>
          </div>
        </div>

        {/* Arrows (visible on hover) */}
        <button
          type="button"
          aria-label="Previous slide"
          onClick={() => {
            const next = (active - 1 + total) % total;
            setLoadedSlides((loaded) => new Set(loaded).add(next));
            setActive(next);
          }}
          className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white opacity-0 backdrop-blur transition-opacity hover:bg-white/30 focus-visible:opacity-100 group-hover:opacity-100"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          aria-label="Next slide"
          onClick={() => {
            const next = (active + 1) % total;
            setLoadedSlides((loaded) => new Set(loaded).add(next));
            setActive(next);
          }}
          className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white opacity-0 backdrop-blur transition-opacity hover:bg-white/30 focus-visible:opacity-100 group-hover:opacity-100"
        >
          <ChevronRight size={20} />
        </button>

        {/* Dots */}
        <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 md:bottom-6">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === active}
              onClick={() => {
                setLoadedSlides((loaded) => new Set(loaded).add(i));
                setActive(i);
              }}
              className="flex min-h-[48px] min-w-[48px] items-center justify-center"
            >
              <span
                aria-hidden="true"
                className={`block h-2 rounded-full transition-all ${i === active ? "w-8 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`}
              />
            </button>
          ))}
        </div>

      </section>

      {/* STATS */}
      <section className="bg-brand-sand">
        <div className="container-x py-20 md:py-28">
          <div className="mx-auto max-w-3xl">
            <div className="border-l-4 border-primary pl-6">
              <p className="max-w-xl leading-relaxed text-left text-brand-ink font-bold text-lg">
                Most Salesforce renewals become difficult long before the quote arrives.
              </p>
            </div>
            <div className="mt-10">
              <SequentialReveal
                lines={[
                  "8/10 companies overpay for software and SaaS agreements without realizing it.",
                  "33% of SaaS licenses sit unused on the shelf — silently draining your budget every renewal cycle.",
                  "Product complexity — constant vendor name changes and confusing feature re-bundling make it nearly impossible to track what you are actually paying for.",
                  "Premature scaling — vendors push you to buy capacity for where you want to be in three years, forcing you to pay for tomorrow's ambition today.",
                ]}
                pause={800}
              />
            </div>
          </div>
        </div>
      </section>

      {/* PARTNER FOR SUCCESS */}
      <section className="relative overflow-hidden bg-cream">
        <div className="absolute -top-16 -left-16 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />
        <div className="container-x relative pt-20 pb-8 md:pt-28 md:pb-6">
          <div className="mx-auto flex max-w-3xl flex-col gap-6 text-center">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">
              Partner for Success
            </h2>
            <p className="m-0 text-lg leading-relaxed text-foreground/80">
              SaaSed is a specialist Salesforce procurement and commercial advisory company. We work with enterprise buyers on Salesforce renewals, contract negotiation, licensing decisions, commercial strategy and cost optimisation — with the aim of improving commercial control and removing cost that serves no purpose.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-background">
        <div className="container-x pt-4 pb-20 md:pt-6 md:pb-28">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                image: skuImg,
                avifSmall: skuImgAvif480,
                avifLarge: skuImgAvif768,
                title: "Contract & SKU Review",
                desc: "Identify pricing inconsistencies, in-term add-ons and opportunities to restructure before renewal.",
              },
              {
                image: auditImg,
                avifSmall: auditImgAvif480,
                avifLarge: auditImgAvif768,
                title: "Software Audit",
                desc: "Gain visibility into usage, shelfware, inactive users and license alignment.",
              },
              {
                image: negotiationImg,
                avifSmall: negotiationImgAvif480,
                avifLarge: negotiationImgAvif768,
                title: "Full Negotiation Support",
                desc: "Strategic commercial support before and during Salesforce renewal discussions.",
              },
            ].map(({ image, avifSmall, avifLarge, title, desc }) => (
              <div
                key={title}
                className="rounded-2xl border border-border bg-card shadow-soft transition-transform hover:-translate-y-1 overflow-hidden"
              >
                <picture>
                  <source
                    type="image/avif"
                    srcSet={`${avifSmall} 480w, ${avifLarge} 768w`}
                    sizes="(min-width: 768px) 33vw, 100vw"
                  />
                  <img
                    src={image}
                    alt={title}
                    width={800}
                    height={480}
                    decoding="async"
                    className="aspect-[5/3] h-48 w-full object-cover"
                    loading="lazy"
                  />
                </picture>
                <div className="p-8">
                  <h3 className="text-xl font-bold text-card-foreground">{title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full bg-black px-8 py-4 text-white shadow-glow transition-transform hover:-translate-y-0.5 font-sans text-sm font-medium uppercase tracking-[0.15em]"
            >
              About our services
            </Link>
          </div>
        </div>
      </section>





      {/* RENEWAL READINESS ASSESSMENT */}
      <section className="relative overflow-hidden bg-[#fae396] font-sans text-[#1a1408]">
        <div className="container-x py-24 md:py-28">
          <div className="text-center">
            <div className="mx-auto flex max-w-[760px] flex-col items-center gap-6">
              <h2 className="m-0 text-balance font-serif text-4xl font-bold leading-[1.15] tracking-normal md:text-5xl">
                Not Sure Where You Currently Stand?
              </h2>
              <p className="m-0 max-w-[720px] text-balance text-lg leading-[1.5] md:text-xl">
                Want more clarity on your Salesforce renewal before discussions begin? Take the Salesforce Renewal Readiness assessment to better understand:
              </p>
            </div>
            <ol className="mx-auto mt-8 grid max-w-[980px] list-none gap-4 p-0 text-left sm:grid-cols-3">
              {[
                "your renewal position",
                "potential leverage gaps",
                "areas of operational and commercial risk",
              ].map((point, index) => (
                <li key={point} className="flex items-start gap-3 rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] px-5 py-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f5cc00] text-[13px] font-medium">
                    {index + 1}
                  </span>
                  <span className="pt-1 text-base leading-6">{point}</span>
                </li>
              ))}
            </ol>
            <div className="mt-11">
              <a
                href="https://saased.involve.me/salesforce-renewal-readiness"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-[#1a1408] px-[34px] py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-[#fffdf0]! transition-colors hover:text-[#f5cc00]!"
              >
                Take the assessment
              </a>
            </div>
            <div className="mt-3.5 text-sm leading-5 opacity-85">Takes 3 minutes.</div>
          </div>
        </div>
      </section>

      {/* WE ONLY ENGAGE — dark banner */}
      <section className="relative bg-foreground text-background">
        <div className="absolute left-0 top-0 h-full w-1.5 bg-primary" />
        <div className="absolute right-0 top-0 h-full w-1.5 bg-accent" />
        <div className="container-x relative py-24 md:py-28">
          <div className="grid items-center gap-10 md:grid-cols-2 md:gap-14">
            <picture>
              <source
                type="image/avif"
                srcSet={`${partnershipAvif480.url} 480w, ${partnershipAvif800.url} 800w`}
                sizes="(min-width: 768px) 384px, 320px"
              />
              <img
                src={partnershipImage.url}
                alt="Team collaborating in a modern boardroom"
                width={1024}
                height={1024}
                loading="lazy"
                decoding="async"
                className="mx-auto max-w-xs rounded-2xl object-cover shadow-soft md:max-w-sm"
              />
            </picture>
            <p className="font-sans text-xl font-bold leading-relaxed text-background/90 md:text-2xl">
              We work with organisations where we believe we can create meaningful impact and long-term value — not short-term transactional wins.
            </p>
          </div>
        </div>
      </section>

      {/* CLIENT SUCCESS STORY */}
      <section className="relative bg-secondary/40">
        <div className="absolute inset-0 -z-0">
          <picture>
            <source type="image/avif" srcSet={`${phmgBgAvif800} 800w, ${phmgBgAvif1600} 1600w`} sizes="100vw" />
            <img src={phmgBg} alt="" role="presentation" width={1920} height={1080} decoding="async" className="h-full w-full object-cover" loading="lazy" />
          </picture>
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="container-x relative py-24 text-center">
          <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
            <div className="h-1.5 w-full bg-gradient-to-r from-primary via-accent to-primary" />
            <div className="p-10 md:p-14">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Client Success Story
              </p>
              <h2 className="mt-3 text-4xl text-card-foreground md:text-5xl">PHMG Case Study</h2>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                Discover how PHMG approached their Salesforce renewal with greater clarity, alignment and commercial control.
              </p>
              <div className="mt-10">
                <Link
                  to="/case-study"
                  aria-label="Read more about the PHMG Salesforce renewal case study"
                  title="Read the PHMG Salesforce renewal case study"
                  className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary/10 px-8 py-4 text-foreground transition-colors hover:bg-primary hover:text-primary-foreground font-sans text-sm font-medium uppercase tracking-[0.15em]"
                >
                  READ THE FULL CASE STUDY
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WANT MORE CLARITY */}
      <section className="relative overflow-hidden bg-cream">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-accent/15 blur-3xl" />
        <div className="container-x relative py-24 md:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
              <div className="h-1.5 w-full bg-gradient-to-r from-accent via-primary to-accent" />
              <div className="p-10 md:p-14">
                <h2 className="text-3xl font-bold text-card-foreground md:text-4xl">
                  Want More Clarity on Your Renewal?
                </h2>
                <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">
                  Talk with <Link to="/about" className="underline decoration-primary decoration-2 underline-offset-4 hover:text-foreground">Anders Gustafsson</Link>, former Salesforce leader and SaaSed Co-Founder.
                </p>
                <div className="mt-10">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-primary-foreground shadow-glow ring-2 ring-primary/30 transition-all hover:-translate-y-0.5 hover:ring-primary/60 font-sans text-sm font-medium uppercase tracking-[0.15em]"
                  >
                    Book a complimentary audit
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#f0e8c8]">
        <div className="container-x py-24 md:py-28">
          <h2 className="m-0 text-center font-serif text-3xl font-bold text-[#1a1408] md:text-4xl">
            FAQ about Salesforce Renewals
          </h2>
          <div aria-hidden="true" className="mx-auto mt-4 h-[2px] w-16 bg-[#f5cc00]" />
          <div className="mx-auto mt-12 flex max-w-3xl flex-col gap-4">
            <details className="group overflow-hidden rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] shadow-[0_1px_2px_rgba(26,20,8,0.06)] transition-colors duration-150 hover:border-[#1a1408]/30">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 md:px-8 md:py-6 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1408] [&::-webkit-details-marker]:hidden">
                <h3 className="m-0 font-sans text-lg font-medium leading-[1.35] tracking-normal text-[#1a1408] md:text-xl">What is Salesforce procurement advisory?</h3>
                <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f0e8c8] transition-colors duration-200 group-open:bg-[#f5cc00]">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" /></svg>
                  </span>
              </summary>
              <div className="flex flex-col gap-4 border-t border-[#f0e8c8] px-6 pb-6 pt-5 md:px-8 md:pb-8">
                  <p className="m-0 text-base leading-[1.7] text-[#1a1408]/85">
                    Salesforce procurement advisory is specialist commercial support for companies that buy Salesforce. An advisor reviews the contract and licence usage, builds the negotiation position and supports the renewal discussions, so the buyer pays for what the business actually uses. SaaSed focuses on Salesforce as a vendor, working with enterprise buyers.
                  </p>
              </div>
            </details>
            <details className="group overflow-hidden rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] shadow-[0_1px_2px_rgba(26,20,8,0.06)] transition-colors duration-150 hover:border-[#1a1408]/30">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 md:px-8 md:py-6 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1408] [&::-webkit-details-marker]:hidden">
                <h3 className="m-0 font-sans text-lg font-medium leading-[1.35] tracking-normal text-[#1a1408] md:text-xl">When should we start preparing for a Salesforce renewal?</h3>
                <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f0e8c8] transition-colors duration-200 group-open:bg-[#f5cc00]">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" /></svg>
                  </span>
              </summary>
              <div className="flex flex-col gap-4 border-t border-[#f0e8c8] px-6 pb-6 pt-5 md:px-8 md:pb-8">
                  <p className="m-0 text-base leading-[1.7] text-[#1a1408]/85">
                    Ideally 12 to 18 months before the contract end date, not when the quote arrives. Starting 8 to 12 months ahead is still a good preparation window, but the leverage you have shrinks with every month after that. Early preparation gives you time to verify your dates, audit your SKUs and build a licence baseline before commercial discussions begin.
                  </p>
                  <p className="m-0 text-base font-medium leading-[1.7] text-[#1a1408]">
                    <Link to="/blog/$slug" params={{ slug: "when-does-your-salesforce-renewal-actually-start" }} className="underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 hover:text-[#1a1408]">Read the full timeline</Link>
                  </p>
              </div>
            </details>
            <details className="group overflow-hidden rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] shadow-[0_1px_2px_rgba(26,20,8,0.06)] transition-colors duration-150 hover:border-[#1a1408]/30">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 md:px-8 md:py-6 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1408] [&::-webkit-details-marker]:hidden">
                <h3 className="m-0 font-sans text-lg font-medium leading-[1.35] tracking-normal text-[#1a1408] md:text-xl">What is a Salesforce SELA?</h3>
                <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f0e8c8] transition-colors duration-200 group-open:bg-[#f5cc00]">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" /></svg>
                  </span>
              </summary>
              <div className="flex flex-col gap-4 border-t border-[#f0e8c8] px-6 pb-6 pt-5 md:px-8 md:pb-8">
                  <p className="m-0 text-base leading-[1.7] text-[#1a1408]/85">
                    A <Link to="/sela" className="underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 hover:text-[#1a1408]">Salesforce SELA</Link>, or Enterprise License Agreement, is a single contract that covers a defined set of Salesforce products under one commercial structure for a fixed term. It can simplify buying, but pricing, scope and renewal terms need careful review, because the structure shapes what you pay at every later renewal.
                  </p>
              </div>
            </details>
            <details className="group overflow-hidden rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] shadow-[0_1px_2px_rgba(26,20,8,0.06)] transition-colors duration-150 hover:border-[#1a1408]/30">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 md:px-8 md:py-6 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1408] [&::-webkit-details-marker]:hidden">
                <h3 className="m-0 font-sans text-lg font-medium leading-[1.35] tracking-normal text-[#1a1408] md:text-xl">How do we find out if we are paying for unused Salesforce licences?</h3>
                <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f0e8c8] transition-colors duration-200 group-open:bg-[#f5cc00]">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" /></svg>
                  </span>
              </summary>
              <div className="flex flex-col gap-4 border-t border-[#f0e8c8] px-6 pb-6 pt-5 md:px-8 md:pb-8">
                  <p className="m-0 text-base leading-[1.7] text-[#1a1408]/85">
                    Compare the licences you hold with real user activity: inactive users, unused products and add-ons bought in-term. A software audit maps each licence to actual usage and contract terms, showing where shelfware sits before the renewal. SaaSed's own tool, <Link to="/dioro" className="underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 hover:text-[#1a1408]">Dioro</Link>, delivers this SKU audit and usage insight.
                  </p>
              </div>
            </details>
          </div>
        </div>
      </section>



    </>
  );
}
